import { useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { AutocompleteInputChangeReason, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { createSearchParams } from 'react-router';
import useSWR from 'swr';

import { AsyncAutocomplete } from '~components';
import KeyTypeEnum from '~enums/keyTypeEnum';
import KeysService from '~features/keys/services/keysService';
import { useFormContainer } from '~hooks';
import Key from '~interfaces/key';
import { cancellationMiddleware } from '~lib/swr';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';

const keysService = getService(KeysService);

export interface Results {
	keyId: string;
}

interface Props {
	organisationId?: string | null;
	onSubmit: (value: Results) => void;
	onCancel: () => void;
}

const AddOfflineKeyForm = ({ organisationId, onSubmit, onCancel }: Props) => {
	const { t } = useTranslation();
	const { setDisabled, formRef } = useFormContainer();
	const { palette } = useTheme();

	const [searchQuery, setSearchQuery] = useState('');
	const [selectedKey, setSelectedKey] = useState<Key | null>(null);

	const cacheKey = keysService.getKeysCacheKey(
		createSearchParams([
			['page', '1'],
			['pageSize', '50'],
			['q', searchQuery],
		]),
		organisationId ?? undefined
	);
	const { data, isLoading, isValidating } = useSWR(
		cacheKey,
		(_args: (string | null)[], options: { signal?: AbortSignal }) =>
			keysService.getKeys({
				page: '1',
				pageSize: '50',
				searchQuery: searchQuery,
				keyType: KeyTypeEnum.SecurePhysicalKey,
				organisationId: organisationId ?? undefined,
				signal: options.signal,
			}),
		{
			keepPreviousData: true,
			use: [cancellationMiddleware],
		}
	);

	const keys = useMemo(() => data?.results ?? [], [data?.results]);
	const options = useMemo(() => {
		if (selectedKey == null || keys.some((candidateKey) => candidateKey.id === selectedKey.id)) {
			return keys;
		}

		return [selectedKey, ...keys];
	}, [keys, selectedKey]);

	useImperativeHandle(
		formRef,
		() => ({
			onSubmit: () => {
				const selectedId = selectedKey?.id;
				if (!selectedId) {
					return;
				}

				onSubmit?.({ keyId: selectedId });
			},
			onCancel: () => onCancel?.(),
		}),
		[selectedKey?.id, onCancel, onSubmit]
	);

	useEffect(() => {
		setDisabled?.(selectedKey == null);
	}, [selectedKey, setDisabled]);

	const handleInputChange = (
		_event: React.SyntheticEvent,
		value: string,
		reason: AutocompleteInputChangeReason
	) => {
		if (reason === 'input' || reason === 'clear') {
			setSearchQuery(value);
		}
	};

	return (
		<Stack spacing={Dimens.spacingMd}>
			<Typography variant='labelSm' color={palette.textPrimary.main}>
				{t('key.fields.secureKey', { ns: 'entities' })}
				<Typography variant='labelSm' color={palette.textBrandTertiary.main} component='span'>
					*
				</Typography>
			</Typography>
			<AsyncAutocomplete<Key>
				value={selectedKey}
				loading={isLoading || isValidating}
				placeholder={t('key.fields.secureKeySelectPlaceholder', { ns: 'entities' })}
				noOptionsText={t('noResults', { ns: 'system' })}
				options={options}
				isOptionEqualToValue={(option, value) => option.id === value.id}
				onInputChange={handleInputChange}
				onOptionSelected={(value) => {
					setSelectedKey(value ?? null);
				}}
				required
				enableInfiniteScroll={false}
				sx={{ width: '100%' }}
			/>
		</Stack>
	);
};

export default AddOfflineKeyForm;
